import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComponentType,
  DataType,
  Layout,
} from '@vgtv/api-client/lib/component';

import { List } from '../../../List/List';
import { Button } from '../../../Button/Button';
import { Spinner } from '../../../Spinner/Spinner';
import { useViewportType } from '../../../utils/useViewportType';
import { getComponentDataFetchLimit } from '../../getComponentDataFetchLimit';
import { COMPONENT_TYPE_LIST_IN_POPUP } from '../../../../consts';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { useAppSelector } from '../../../../modules/hooks';
import { selectAssetIdsForWatchtimeHistory } from '../../../../modules/watchtime';
import { useSortAssets } from '../../useSortAssets';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { fetchSvpCategories } from '../../../../modules/categories';
import { useComponentCategories } from '../../../utils/useComponentCategories';
import { isSvpCategory } from '../../../../modules/helpers';
import { CategoryItem } from '../../common/CategoryItem/CategoryItem';

import type { ContinueWatchingComponent } from '@vgtv/api-client/lib/component/continue_watching';
import type {
  AssetSliderComponent,
  CategorySliderComponent,
  SvpEpisodeSliderComponent,
} from '@vgtv/api-client/lib/component/slider';
import type { VFC } from 'react';

import * as styles from './ComponentPopupContent.module.scss';

type Props = {
  componentConfig:
    | SvpEpisodeSliderComponent
    | AssetSliderComponent
    | CategorySliderComponent
    | ContinueWatchingComponent;
};

export const ComponentPopupContent: VFC<Props> = ({ componentConfig }) => {
  const watchtimeAssetIds = useAppSelector(selectAssetIdsForWatchtimeHistory);

  const { viewportType } = useViewportType();
  const { t } = useTranslation();

  const fetchLimit = getComponentDataFetchLimit({
    componentType: COMPONENT_TYPE_LIST_IN_POPUP,
    dataType: componentConfig.data.type,
    componentLayout: Layout.VERTICAL,
    viewportType,
  });

  const { isFetching, hasNextPage, fetchNextPage } = useComponentDataFetch({
    componentConfig,
    fetchAction:
      componentConfig.data.type === DataType.ASSETS
        ? fetchSvpAssets
        : fetchSvpCategories,
    fetchLimit,
    assetIds:
      componentConfig.type === ComponentType.CONTINUE_WATCHING
        ? watchtimeAssetIds
        : undefined,
  });

  function onButtonClick() {
    fetchNextPage();
  }

  const { id, header } = componentConfig;

  const assets = useComponentAssets(id);
  const sortedAssets = useSortAssets(assets, componentConfig.type);
  const categories = useComponentCategories(id);
  let entities;

  if (componentConfig.data.type === DataType.ASSETS) {
    entities = sortedAssets;
  } else {
    entities = categories;
  }

  return (
    <Fragment>
      <List header={header} className={styles.list}>
        {entities.map((entity) => {
          if (isSvpCategory(entity)) {
            return (
              <CategoryItem
                key={`category:${entity.id}`}
                replacePath
                category={entity}
                componentType={ComponentType.LIST}
              />
            );
          }

          return (
            <AssetItem
              key={`asset:${entity.id}`}
              replacePath
              asset={entity}
              componentConfig={
                componentConfig as
                  | SvpEpisodeSliderComponent
                  | AssetSliderComponent
                  | ContinueWatchingComponent
              }
            />
          );
        })}
      </List>
      {hasNextPage && (
        <div className={styles.buttonContainer}>
          {isFetching ? (
            <Button component="span" variant="text">
              <Spinner size="small" />
            </Button>
          ) : (
            <Button
              className={styles.buttonLabel}
              variant="contained"
              onClick={onButtonClick}
            >
              {t('Load more')}
            </Button>
          )}
        </div>
      )}
    </Fragment>
  );
};
